import { observer } from 'mobx-react';
import React, { useEffect, useRef, useState } from 'react';
import C from './colors';
import { getPictureThumbnailUrl } from '../../pictureUrls';

function Heart({ filled }) {
    return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
                d="M8 13.5C8 13.5 1.5 9.5 1.5 5.5C1.5 3.5 3 2 5 2C6.5 2 7.5 3 8 4C8.5 3 9.5 2 11 2C13 2 14.5 3.5 14.5 5.5C14.5 9.5 8 13.5 8 13.5Z"
                fill={filled ? '#fff' : 'none'}
                stroke="#fff"
                strokeWidth="1.5"
                strokeLinejoin="round"
            />
        </svg>
    );
}

const CustomerImageGallery = observer(({
    picture,
    isSelected,
    albumClaimed,
    showImageName,
    handlePictureSelect,
    handlePreview,
    watermark,
    cols,
    multiMode,
    tagDefinitions = [],
    appliedTagIds = [],
}) => {
    const tileRef = useRef(null);
    const [inView, setInView] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const thumbnailUrl = getPictureThumbnailUrl(picture);
    const appliedTags = appliedTagIds
        .map(tagId => tagDefinitions.find(item => String(item.id) === String(tagId)))
        .filter(Boolean);

    useEffect(() => {
        const el = tileRef.current;
        if (!el) return;
        const io = new IntersectionObserver(
            ([entry]) => {
                const visible = entry.isIntersecting;
                setInView(visible);
                if (!visible) setLoaded(false); // remove from DOM → browser frees memory
            },
            { rootMargin: '400px 0px' } // 400px buffer above/below viewport
        );
        io.observe(el);
        return () => io.disconnect();
    }, []);

    const handleClick = () => {
        if (albumClaimed) return;
        if (multiMode) {
            handlePictureSelect(picture.id, !isSelected);
        } else {
            handlePreview();
        }
    };

    return (
        <div
            ref={tileRef}
            data-testid={`customer-photo-${picture.id}`}
            data-selected={isSelected ? 'true' : 'false'}
            onClick={handleClick}
            style={{
                position: 'relative',
                minWidth: 0,
                aspectRatio: '4/5',
                borderRadius: 4,
                overflow: 'hidden',
                cursor: albumClaimed ? 'default' : 'pointer',
                boxShadow: isSelected ? `inset 0 0 0 3px ${C.brand}` : 'none',
                transition: 'box-shadow .15s',
                background: `linear-gradient(135deg, ${C.paper2} 0%, ${C.hair} 100%)`,
            }}
        >
            {inView && (
                <>
                    {/* shimmer while loading */}
                    {!loaded && thumbnailUrl && (
                        <div style={{
                            position: 'absolute', inset: 0,
                            background: 'linear-gradient(90deg, #E8E8E8 0%, #E8E8E8 30%, #fff 50%, #E8E8E8 70%, #E8E8E8 100%)',
                            backgroundSize: '300% 100%',
                            animation: 'shimmer 1.6s ease-in-out infinite',
                        }} />
                    )}

                    {/* actual image */}
                    {thumbnailUrl && (
                        <img
                            src={thumbnailUrl}
                            alt=""
                            decoding="async"
                            onLoad={() => setLoaded(true)}
                            style={{
                                position: 'absolute', inset: 0,
                                width: '100%', height: '100%',
                                objectFit: 'cover', objectPosition: 'center',
                                opacity: loaded ? 1 : 0,
                                transition: 'opacity .3s ease',
                            }}
                        />
                    )}
                </>
            )}

            {/* light overlay */}
            <div style={{
                position: 'absolute', inset: 0,
                background: 'radial-gradient(ellipse at 30% 25%, rgba(255,255,255,.15), transparent 55%)',
                pointerEvents: 'none',
            }} />

            {/* watermark */}
            {watermark && (
                <div style={{
                    position: 'absolute', inset: 0,
                    color: 'rgba(255,255,255,.4)',
                    fontSize: cols === 2 ? 16 : 11,
                    fontWeight: 600, letterSpacing: 3,
                    transform: 'rotate(-15deg)',
                    display: 'grid', placeItems: 'center',
                    pointerEvents: 'none',
                    fontFamily: '"Instrument Serif", serif',
                }}>图派</div>
            )}

            {/* non-multiMode: zoom button to preview */}
            {!multiMode && !albumClaimed && (
                <button
                    data-testid={`customer-preview-${picture.id}`}
                    onClick={e => { e.stopPropagation(); handlePreview(); }}
                    style={{
                        position: 'absolute', top: 6, left: 6,
                        width: 26, height: 26, borderRadius: 13, border: 'none',
                        background: 'rgba(0,0,0,.35)',
                        backdropFilter: 'blur(8px)',
                        WebkitBackdropFilter: 'blur(8px)',
                        color: '#fff', cursor: 'pointer',
                        display: 'grid', placeItems: 'center',
                        fontSize: 14, lineHeight: 1,
                        zIndex: 2,
                    }}>⌕</button>
            )}

            {/* non-multiMode: heart select button */}
            {!multiMode && !albumClaimed && (
                <div
                    data-testid={`customer-select-${picture.id}`}
                    onClick={e => { e.stopPropagation(); handlePictureSelect(picture.id, !isSelected); }}
                    style={{
                        position: 'absolute', bottom: 8, right: 8,
                        width: 32, height: 32, borderRadius: 16,
                        background: isSelected ? C.brand : 'rgba(0,0,0,.35)',
                        backdropFilter: 'blur(8px)',
                        WebkitBackdropFilter: 'blur(8px)',
                        display: 'grid', placeItems: 'center',
                        transition: 'all .2s',
                        transform: isSelected ? 'scale(1.05)' : 'scale(1)',
                        boxShadow: isSelected ? '0 4px 12px rgba(247,119,176,.5)' : 'none',
                        zIndex: 2, cursor: 'pointer',
                    }}>
                    <Heart filled={isSelected} />
                </div>
            )}

            {/* multiMode: checkbox indicator */}
            {multiMode && !albumClaimed && (
                <div style={{
                    position: 'absolute', bottom: 8, right: 8,
                    width: 24, height: 24, borderRadius: 12,
                    background: isSelected ? C.brand : 'rgba(0,0,0,.35)',
                    border: `2px solid ${isSelected ? C.brand : 'rgba(255,255,255,.6)'}`,
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                    display: 'grid', placeItems: 'center',
                    transition: 'all .15s',
                    zIndex: 2,
                }}>
                    {isSelected && (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="m5 13 4 4L19 7"/>
                        </svg>
                    )}
                </div>
            )}

            {/* visible tag labels */}
            {appliedTags.length > 0 && (
                <div data-testid={`customer-tag-badges-${picture.id}`} style={{
                    position: 'absolute', top: 6, right: 6,
                    display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4,
                    maxWidth: 'calc(100% - 12px)', zIndex: 3,
                    pointerEvents: 'none',
                }}>
                    {appliedTags.map(tag => (
                        <div
                            key={tag.id}
                            title={tag.name}
                            data-testid={`customer-tag-badge-${picture.id}-${tag.id}`}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 5,
                                maxWidth: '100%', padding: '4px 7px 4px 5px', borderRadius: 10,
                                background: 'rgba(26,22,20,.78)', color: '#fff',
                                boxShadow: '0 2px 8px rgba(0,0,0,.24)',
                                backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
                                fontFamily: C.sans, fontSize: 9.5, fontWeight: 600, lineHeight: 1,
                            }}
                        >
                            <span style={{
                                width: 7, height: 7, borderRadius: 4, flexShrink: 0,
                                background: tag.color || '#fff',
                                boxShadow: '0 0 0 1px rgba(255,255,255,.32)',
                            }} />
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {tag.name}
                            </span>
                        </div>
                    ))}
                </div>
            )}

            {/* claimed badge */}
            {albumClaimed && isSelected && (
                <div style={{
                    position: 'absolute', top: 0, right: 0,
                    background: C.brand,
                    color: '#fff', fontSize: 10, fontWeight: 600,
                    padding: '3px 6px',
                    fontFamily: '"Inter", sans-serif',
                }}>已选</div>
            )}

            {/* filename */}
            {showImageName && (
                <div style={{
                    position: 'absolute', bottom: 0, left: 0, right: 0,
                    padding: '14px 6px 4px',
                    fontFamily: 'ui-monospace, monospace', fontSize: 8,
                    color: '#fff',
                    background: 'linear-gradient(transparent, rgba(0,0,0,.5))',
                    pointerEvents: 'none',
                }}>{picture.fileName}</div>
            )}
        </div>
    );
});

export { CustomerImageGallery };
